import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWaitingTickets } from '@/lib/tickets';

import {
  RANGES,
  useAppStoreOverview,
  useAppStoreStatus,
  useSyncAppStore,
  type AppStoreMoney,
  type AppStoreOverview,
  type AppStoreSyncRun,
} from '@/lib/app-store-connect';
import { dateRange, day, dayInYear } from '@/lib/dates';

/**
 * The Overview.
 *
 * ⚠️ **This screen used to show the design file's sample figures** — 14,905
 * downloads, $31,470 in proceeds, a chart, a table of apps, three Signals — and
 * every one of them was a number from `Sonasi Admin.dc.html`, not from this
 * studio. It read as the most finished screen in the admin precisely because it
 * had percentages and little arrows on it.
 *
 * Numbers on an admin screen get quoted. Somebody reads "Refund rate 0.8%" at a
 * meeting, or puts $31,470 in a plan, and nothing on the page ever said where
 * it came from except a comment in the source. So the figures are gone rather
 * than emptied out: a chart with no line and a table with no rows is still the
 * shape of an answer.
 *
 * What is here instead is the setup, in full. **Every step below was read off
 * Apple's own documentation rather than written from memory** — the links are
 * in `SOURCES` so the next person can check rather than trust. Two of the facts
 * are ones nobody would guess: Sales and Finance is closed to an Individual
 * key, and the private key can be downloaded exactly once.
 *
 * The layout is not lost — it is in `design/Sonasi Admin.dc.html` and in git at
 * `2431a92`. Rebuilding it is part of wiring App Store Connect, where the shape
 * of a report can be decided against the figures Apple actually returns.
 */

/** Apple's own pages, so the steps below can be checked rather than believed. */
const SOURCES = [
  [
    'Creating API keys',
    'https://developer.apple.com/documentation/appstoreconnectapi/creating-api-keys-for-app-store-connect-api',
  ],
  [
    'Generating tokens',
    'https://developer.apple.com/documentation/appstoreconnectapi/generating-tokens-for-api-requests',
  ],
  [
    'Sales and Finance',
    'https://developer.apple.com/documentation/appstoreconnectapi/sales-and-finance',
  ],
  /*
   * Added after the role step was found to be too narrow — it read "give it the
   * Finance role", as though Finance were the only one, so a studio already
   * holding a suitable key was being sent to make another.
   *
   * ⚠️ The first attempt to widen it went further wrong than the original. It
   * replaced one narrow claim with "Account Holder and Admin reach everything",
   * which appears nowhere on this page — Apple describes Admin as having "many
   * of the same responsibilities as the Account Holder" and names only Finance
   * against Sales and Trends — and a test was written pinning that invented
   * sentence in place. On a screen whose whole promise is that every step was
   * read off Apple's own pages, writing one that was not, and then guarding it,
   * is worse than the narrowness it was fixing. The step now quotes what the
   * page says and sends the reader here for what it does not.
   */
  [
    'Role permissions',
    'https://developer.apple.com/help/app-store-connect/reference/role-permissions/',
  ],
];

const STEPS: Array<[string, string]> = [
  [
    'Sign in to App Store Connect as the Account Holder or an Admin',
    'Apple gives the required role for generating team keys as "Account Holder or Admin". It has to be a team key: of the Sales and Finance endpoints Apple says "These endpoints require a Team key and aren\u2019t usable with an Individual key" — so a key generated from a personal profile works, and can never answer the one question this screen asks.',
  ],
  [
    'Users and Access → Integrations → App Store Connect API',
    'Keep the Team Keys tab selected. The Issuer ID appears near the top of that page.',
  ],
  [
    'Generate API Key, name it, give it a role that can read Sales and Trends',
    'The name is for your own reference; the role decides what the key may read. Finance is the one Apple names for this: it "can view all apps in Payments and Financial Reports, Sales and Trends, and App Analytics". Apple describes the other roles without saying whether they reach that report, so check the role permissions table linked below rather than guessing — a key whose role cannot reach it is answered 403, which reads like a broken token and is not one.',
  ],
  [
    'Copy the Key ID, then download the private key',
    'The .p8 file can be downloaded once and once only. Apple keeps no copy: if it is lost, the key has to be revoked and a new one generated.',
  ],
  [
    'Find the Vendor Number',
    'A different page and easy to miss: Payments and Financial Reports, then the Reports tab at the top — the number is in the top left, under the Legal Entity Name. Sales reports are requested per vendor, so nothing can be fetched without it.',
  ],
  [
    'Enter all four in Settings → Connections',
    'The App Store Connect card there takes the Issuer ID, the Key ID, the Vendor Number and the .p8 file. They are stored on the server, encrypted, and never shown again; the .p8 is read once from your disk and sent over your admin session, not kept in the browser. Press Test connection there to have the server ask Apple for a report with them. The four environment variables (APPSTORE_ISSUER_ID and the rest, described in apps/api/.env.example) still work as a fallback for a server nobody has typed them into — but what is saved in Connections is what is used, and saving there takes effect without a restart.',
  ],
];

const THEN: Array<[string, string]> = [
  [
    'A token per burst of requests',
    'Signed ES256 with the .p8, carrying the Issuer ID and an audience of appstoreconnect-v1. For requests like these Apple rejects a token older than twenty minutes — the longer-lived kind it allows is for a fixed list of resources that sales reports are not on — and asks that one token be reused until it expires rather than minted per request.',
  ],
  [
    'GET /v1/salesReports',
    'Filtered by vendor number, frequency, report type and sub-type. It answers with a gzipped file, not JSON, and it answers 429 when asked too often.',
  ],
  [
    'Downloads, proceeds and subscriptions',
    'All from that one endpoint, under different report types — SALES for units and proceeds, SUBSCRIPTION and SUBSCRIBER for the rest.',
  ],
];

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-2">
    <span className="eyebrow">{title}</span>
    {children}
  </div>
);

const Steps = ({ items, numbered }: { items: Array<[string, string]>; numbered?: boolean }) => (
  <ol className="m-0 flex list-none flex-col gap-2.5 p-0">
    {items.map(([title, note], at) => (
      <li key={title} className="flex gap-2.5">
        {numbered ? (
          <span
            className="mt-px shrink-0 font-mono text-[10.5px] tabular-nums"
            style={{ color: 'var(--accent)' }}
            aria-hidden="true"
          >
            {String(at + 1).padStart(2, '0')}
          </span>
        ) : null}
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[12.5px] leading-[1.45] font-medium">{title}</span>
          <span className="text-[11.5px] leading-[1.55]" style={{ color: 'var(--muted)' }}>
            {note}
          </span>
        </span>
      </li>
    ))}
  </ol>
);

/**
 * A circular arrow, for the two buttons that ask the server to go again.
 *
 * Inline rather than from a shared module: this admin draws its icons as inline
 * SVG wherever it needs one, and a one-path glyph used twice on one screen is
 * not yet a shared component.
 *
 * `aria-hidden`, because it says nothing the label does not. An icon announced
 * beside the word it decorates is the same thing read twice.
 */
const RetryIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path
      d="M13.6 1.9v2.9h-2.9"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The same 12px box as `RetryIcon`, turning.
 *
 * The button already swapped its label and dimmed itself while a sync ran, and
 * that was reported as no feedback at all — a disabled button with an arrow on
 * it looks like a button that did not take the click. Movement is the part
 * people read as "working"; the label is what says *what* is working.
 */
const SpinnerIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    data-spinner
    style={{ animation: 'sonasi-spin 0.7s linear infinite' }}
  >
    {/* The track, so the moving arc has something to move against. */}
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" opacity="0.25" />
    <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

/** While the server is being asked. Says nothing about the answer. */
const Asking = () => (
  // Announced, because the screen replaces itself when the answer lands and a
  // reader who is not looking at it gets no other signal that it did.
  <div className="flex flex-col gap-1.5" role="status" aria-live="polite">
    <h2 className="m-0 text-[15px] font-semibold tracking-[-0.01em]">Checking App Store Connect</h2>
    <p className="m-0 text-[12.5px] leading-[1.6]" style={{ color: 'var(--muted)' }}>
      Asking the server whether it holds the credentials.
    </p>
  </div>
);

/**
 * When the server could not be asked at all.
 *
 * The wording matters more here than anywhere else on the screen: this is not
 * "not connected". The studio may be perfectly configured and this browser
 * unable to reach the API, or signed out. Drawing the setup guide here would
 * send somebody to revoke a working key and generate a new one.
 */
const Unknown = ({ onRetry }: { onRetry: () => void }) => (
  <div className="flex flex-col items-start gap-2.5" role="status" aria-live="polite">
    <h2 className="m-0 text-[15px] font-semibold tracking-[-0.01em]">
      Could not check App Store Connect
    </h2>
    <p className="m-0 text-[12.5px] leading-[1.6]" style={{ color: 'var(--muted)' }}>
      The server did not answer, so this screen does not know whether the credentials are in place.
      This is not the same as them being missing — the session may have expired, or the API may be
      unreachable from here. Nothing about the setup has changed because of it.
    </p>
    {/*
      No disabled state and no "Trying…" label, and that is measured rather than
      assumed. Both were written here first, on the belief that a failed query
      keeps `status: 'error'` while it refetches — so the screen would look
      identical before and after the press, and the only way to tell would be to
      press again.

      It is not what happens. Refetching a query that has no data puts it back
      to pending, so pressing this replaces the whole panel with "Checking App
      Store Connect" — the feedback the disabled state was invented to supply,
      already there — and this branch does not render at all while it is
      happening. The label was unreachable code guarding an unreal problem.
    */}
    {/*
      The size comes from utilities, not from `btn-outline`.

      That class sets a background, a colour, a border and a radius and nothing
      else — every other button in this admin adds its own height, padding and
      type size on top. Written without them this rendered as a hairline box
      shrink-wrapped around the word, which looked like a mistake because it
      was one. `.btn` was the first attempt and does not exist at all.
    */}
    <button
      type="button"
      className="btn-outline focus-ring inline-flex min-h-8 cursor-pointer items-center gap-1.5 px-3 py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
      onClick={onRetry}
    >
      <RetryIcon />
      Try again
    </button>
  </div>
);

/**
 * A number the studio will read out loud.
 *
 * Grouped with a thin space rather than a comma or a full stop, because the two
 * conventions mean opposite things — `31.470` is thirty-one thousand in Berlin
 * and thirty-one point four seven in Boston — and this admin is read in both.
 */
const grouped = (value: number): string => value.toLocaleString('en-US').replace(/,/g, '\u2009');

/**
 * Money, printed with its currency and never converted.
 *
 * `Intl.NumberFormat` with the currency code, so a reader sees the symbol they
 * expect. No exchange rate anywhere: this screen has no rates and no date to
 * apply them at, and a converted figure is a claim about a market rather than
 * a copy of what Apple said.
 */
const money = ({ currency, amount }: AppStoreMoney): string => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} ${currency}`;
  try {
    return (
      new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 })
        .format(value)
        /*
         * Regrouped with the same thin space `grouped` uses. The two lived side
         * by side in one row of figures — "Downloads" separating its thousands
         * with a thin space, "Proceeds" with a comma — on the argument that a
         * comma means different things in the languages this admin is read in.
         * If that is true of 14 905 downloads it is more true of $31 470.
         */
        .replace(/,/g, '\u2009')
    );
  } catch {
    // An unrecognised code — Apple has a few. Better the code than a throw.
    return `${amount} ${currency}`;
  }
};

/**
 * The one figure that is a total, with what it is a total *of* beside it.
 *
 * The range is not decoration. This screen once showed 14,905 downloads and
 * $31,470 in proceeds with nothing saying over what period, or from where —
 * they were the design file's sample numbers. A total with no range is a number
 * the reader will assume covers a month.
 */
const Figure = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <span className="eyebrow">{label}</span>
    <span className="text-[22px] leading-none font-semibold tracking-[-0.02em] tabular-nums">
      {children}
    </span>
  </div>
);

/**
 * Downloads per day, drawn as bars.
 *
 * Bars rather than a line, and no axis: with five days of data a line implies a
 * trend that five points cannot support, and an axis implies a scale this has
 * not earned. Each bar carries its own number in the title, so the shape is a
 * summary and the value is still readable.
 */
/** The design's panel: a hairline-bordered card at 12px radius. */
const Panel = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div
    className={`rounded-xl border ${className}`}
    style={{ background: 'var(--panel)', borderColor: 'var(--line)' }}
  >
    {children}
  </div>
);

/**
 * The strip of figures across the top: one panel, cells divided by hairlines.
 *
 * ⚠️ Three of the design's five have no source here — active subscriptions,
 * free-to-paid, refund rate. They keep their place and say **what is missing**
 * where a figure would be. Dropping them would claim they do not exist;
 * printing a zero would claim they are zero, and a zero is every bit as much a
 * fabricated figure as the 14,905 this screen was built to remove.
 */
const Stats = ({ overview }: { overview: AppStoreOverview }) => {
  /*
   * ⚠️ Every empty figure reads **None**, and the note underneath is what says
   * which kind of nothing it is.
   *
   * They said "Not fetched" for a round, which was more precise and less
   * consistent: three of the five carried a different word from the other two,
   * in one strip, at a different size. The word is now the same everywhere and
   * the distinction moved down a line — "we asked and there were none" and "we
   * have not asked" are still different sentences, they are just no longer
   * different headlines.
   *
   * The cost is real and worth naming: **None** on its own reads as a claim
   * about the studio, and for three of these it is a claim about this server.
   * The note is doing load-bearing work under each of them.
   */
  /*
   * ⚠️ Notes are for the studio, not for whoever wrote this.
   *
   * They said things like "Needs the SUBSCRIPTION report, which this does not
   * ask for yet" and "Refunds are in the financial reports, not in Sales and
   * Trends" — true, and none of anybody's business who came here to see how
   * many people downloaded the app. A screen that explains its own plumbing to
   * every visitor is a screen that has confused its author's questions for its
   * reader's.
   *
   * The precision has not gone; it has moved to `why`, which is a tooltip. Two
   * different readers, two different places: the note is what the figure means,
   * and the tooltip is why it is empty or how it is counted.
   */
  const period = { '7d': '7 days', mtd: 'this month', '30d': '30 days', '90d': '90 days' };

  const cells: Array<{
    label: string;
    value: React.ReactNode;
    note: string;
    why?: string;
  }> = [
    {
      label: 'Downloads',
      value: grouped(overview.downloads),
      /*
       * Always the comparison, never a date range.
       *
       * This note used to fall back to the period's dates whenever there was
       * nothing to compare against — so the same cell said "−89% vs the period
       * before" at 30 days and "10 Jun – 7 Sept 2026" at 90, and a reader had
       * to work out that those were answers to different questions. The dates
       * are already beside the period tabs; what is missing here is the reason
       * the comparison is missing, which is that the 90 days before these 90
       * were never fetched.
       */
      note:
        overview.previousDownloads === null
          ? 'no earlier period to compare'
          : /*
             * The third state, and the one the 7-day view lands on for a
             * studio this size: `change` answers "—" for nothing against
             * nothing, because there is no percentage between two zeroes — and
             * "— vs the period before" reads as a bug rather than a quiet week.
             */
            overview.downloads === 0 && overview.previousDownloads === 0
            ? 'none in either period'
            : `${change(overview.downloads, overview.previousDownloads).text} vs the period before`,
      why:
        overview.previousDownloads === null
          ? 'Nothing was fetched for the period before this one, and comparing against days nobody asked for would read as a fall to zero.'
          : 'New customers only. Updates and re-downloads are not counted, so this does not move when a release ships.',
    },
    {
      label: 'Proceeds',
      value:
        overview.proceeds.length === 0 ? (
          <span style={{ color: 'var(--muted)' }}>None</span>
        ) : (
          <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
            {overview.proceeds.map((one) => (
              <span key={one.currency}>{money(one)}</span>
            ))}
          </span>
        ),
      note: "after Apple's commission",
      why: 'Shown in the currency Apple pays in. Never converted — this screen has no exchange rate.',
    },
    {
      label: 'Active subs',
      value:
        overview.activeSubscriptions === null ? (
          <span style={{ color: 'var(--muted)' }}>None</span>
        ) : (
          grouped(overview.activeSubscriptions)
        ),
      note:
        overview.activeSubscriptions === null || overview.activeSubscriptionsOn === null
          ? 'not available yet'
          : `on ${day(overview.activeSubscriptionsOn)}`,
      why:
        overview.activeSubscriptions === null
          ? 'Apple has not sent a subscription report for this period yet.'
          : 'How many were active on that one day — a count at a moment, not a total for the period.',
    },
    {
      label: 'Free → paid',
      value:
        overview.trialConversion === null ? (
          <span style={{ color: 'var(--muted)' }}>None</span>
        ) : (
          `${overview.trialConversion}%`
        ),
      note:
        overview.trialConversion === null
          ? 'not available yet'
          : `${grouped(overview.trialsConverted ?? 0)} of ${grouped(overview.trialsStarted ?? 0)} trials`,
      why:
        overview.trialsStarted === null
          ? 'Apple has not sent any subscription activity for this period yet.'
          : overview.trialsStarted === 0
            ? 'Nobody started a free trial in this period, so there is no rate to give.'
            : 'Trials that started in this period against trials that became paid in it. A trial begun earlier converts later, so the two are not the same people.',
    },
    {
      label: 'Refund rate',
      value: <span style={{ color: 'var(--muted)' }}>None</span>,
      note: 'not available yet',
      why: 'Refunds come from a different Apple report, which this does not fetch.',
    },
  ];

  return (
    <Panel className="grid overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))' }}>
        {cells.map((cell) => (
          <div
            key={cell.label}
            title={cell.why}
            className="flex min-w-0 flex-col gap-1 border-r px-3.5 py-3 last:border-r-0"
            style={{ borderColor: 'var(--line)' }}
          >
            <span className="text-[13px] font-semibold tracking-[-0.01em]">{cell.label}</span>
            <span className="text-[21px] leading-none font-semibold tracking-[-0.03em] tabular-nums">
              {cell.value}
            </span>
            <span className="text-[11.5px] leading-[1.4]" style={{ color: 'var(--muted)' }}>
              {cell.note}
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
};

/**
 * The design's chart: an area under a line, on a 100×34 grid.
 *
 * Values are scaled to the tallest point in the series rather than to a fixed
 * ceiling — the design's own sample data is pre-normalised to 0–100 and real
 * data is not, so scaling to the maximum is what makes a studio with thirteen
 * downloads and a studio with thirteen thousand both draw a readable line.
 *
 * ⚠️ A series of nothing but zeroes is drawn **on the floor**, not down the
 * middle. Down the middle is where it used to go, on the reasoning that a flat
 * line would otherwise read as zero — but the line is only half of this: the
 * area under it is filled, so a week with no downloads at all rendered as a
 * solid block filling most of the card. It read as the largest week on the
 * screen. A week of nothing is a flat line along the axis and no fill at all,
 * which is what nothing looks like.
 */
const chartPath = (values: number[], close: boolean): string => {
  if (values.length === 0) return '';
  const most = Math.max(...values);
  const points = values.map((value, at) => {
    const x = values.length === 1 ? 50 : (at / (values.length - 1)) * 100;
    const y = most > 0 ? 34 - (value / most) * 30 : 34;
    return `${x.toFixed(2)} ${y.toFixed(2)}`;
  });
  let d = `M${points.join('L')}`;
  if (close) d += 'L100 34L0 34Z';
  return d;
};

/** Four evenly spaced dates, so the axis says what the line is over. */
const axisOf = (days: AppStoreOverview['byDay']): string[] => {
  if (days.length === 0) return [];
  if (days.length <= 4) return days.map((one) => dayInYear(one.reportDate));
  return [0, 1, 2, 3].map((at) =>
    dayInYear(days[Math.round((at / 3) * (days.length - 1))].reportDate),
  );
};

type Metric = 'downloads' | 'proceeds';

/**
 * The chart card, with the design's two metric tabs.
 *
 * Proceeds are plotted in **one** currency — the one the studio earns most in —
 * and the title says which. A single line cannot carry several currencies, and
 * converting them would be a claim about exchange rates on dates this screen
 * does not have.
 */
const Chart = ({
  overview,
  metric,
  onMetric,
  updated,
}: {
  overview: AppStoreOverview;
  metric: Metric;
  onMetric: (metric: Metric) => void;
  /** When the server last managed to ask Apple, as "2 hours ago". */
  updated: string | null;
}) => {
  const currency = overview.proceeds[0]?.currency ?? null;
  const series =
    metric === 'downloads'
      ? overview.byDay.map((day) => day.downloads)
      : overview.byDay.map((day) =>
          Number(day.proceeds.find((one) => one.currency === currency)?.amount ?? 0),
        );
  const total =
    metric === 'downloads'
      ? grouped(overview.downloads)
      : currency && overview.proceeds[0]
        ? money(overview.proceeds[0])
        : 'None';

  return (
    <Panel className="min-w-0 px-4 py-3.5">
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="text-[13px] font-semibold tracking-[-0.01em]">
          {metric === 'downloads' ? 'Downloads' : `Proceeds${currency ? ` · ${currency}` : ''}`}
        </span>
        {/*
          The design's own `Downloads · 2 hours ago`. This is where "is what I
          am looking at current" belongs — attached to the thing being looked
          at, rather than as a status line above the page.
        */}
        {updated ? (
          <span className="font-mono text-[10.5px]" style={{ color: 'var(--dim)' }}>
            {updated}
          </span>
        ) : null}
        <span className="text-[18px] font-semibold tracking-[-0.025em] tabular-nums">{total}</span>
        <span
          className="ml-auto inline-flex rounded-lg border p-0.5"
          style={{ background: 'var(--panel2)', borderColor: 'var(--line)' }}
        >
          {(['downloads', 'proceeds'] as const).map((one) => (
            <button
              key={one}
              type="button"
              onClick={() => onMetric(one)}
              aria-pressed={metric === one}
              className="focus-ring min-h-[26px] cursor-pointer rounded-md border-0 px-2.5 text-[11.5px] capitalize"
              style={
                metric === one
                  ? { background: 'var(--in)', color: 'var(--text)' }
                  : { background: 'transparent', color: 'var(--muted)' }
              }
            >
              {one}
            </button>
          ))}
        </span>
      </div>

      <svg
        viewBox="0 0 100 34"
        preserveAspectRatio="none"
        role="img"
        aria-label={`${metric} per day, ${dateRange(overview.from, overview.to)}`}
        className="mt-2.5 block w-full overflow-visible"
        style={{ height: 132, color: 'var(--accent)' }}
      >
        <defs>
          <linearGradient id="sonasi-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: 'var(--accent)', stopOpacity: 0.28 }} />
            <stop offset="100%" style={{ stopColor: 'var(--accent)', stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        <path
          d="M0 8.5H100M0 17H100M0 25.5H100"
          style={{ stroke: 'var(--line)' }}
          strokeWidth="0.3"
          strokeDasharray="1 2"
        />
        <path d={chartPath(series, true)} fill="url(#sonasi-area)" />
        <path
          d={chartPath(series, false)}
          fill="none"
          stroke="currentColor"
          strokeWidth="0.7"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <div
        className="mt-1.5 flex justify-between font-mono text-[9.5px]"
        style={{ color: 'var(--dim)' }}
      >
        {axisOf(overview.byDay).map((label, at) => (
          <span key={`${label}-${at}`}>{label}</span>
        ))}
      </div>
      {metric === 'proceeds' && !currency ? (
        <p className="m-0 mt-2 text-[11.5px]" style={{ color: 'var(--muted)' }}>
          No proceeds in this period — every download was of a free app.
        </p>
      ) : null}
    </Panel>
  );
};

/**
 * The state of the last fetch, and the button to fetch again, in one line.
 *
 * ⚠️ This was a panel at the bottom of the page for a round, under the markets
 * cards — the one control on the screen a person actually presses, below
 * everything they came to read. Two things wanted fixing: it should be where
 * the eye already is, and pressing it should not mean scrolling past the
 * figures to find it.
 *
 * So it sits in the header row beside the period tabs. The sentence is short
 * enough to scan; the part that is long — whatever Apple said — appears only
 * when something is actually wrong, because that is the only time it is worth
 * a line of its own.
 */
const SyncBar = ({
  lastSync,
  onSync,
  syncing,
  children,
}: {
  lastSync: AppStoreSyncRun | null;
  onSync: () => void;
  syncing: boolean;
  children: React.ReactNode;
}) => {
  const fine = lastSync !== null && ['report', 'noSales', 'notReady'].includes(lastSync.outcome);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          className="btn-outline focus-ring inline-flex min-h-[26px] cursor-pointer items-center gap-1.5 rounded-md px-2.5 text-[11.5px] disabled:cursor-default disabled:opacity-50"
          onClick={onSync}
          disabled={syncing}
        >
          {syncing ? <SpinnerIcon /> : <RetryIcon />}
          {syncing ? 'Asking Apple…' : 'Sync now'}
        </button>
        {/*
          ⚠️ Nothing here on an ordinary day, and that is the point.
          
          This line used to read "Apple has that day, and there were no sales on
          it · 2026-09-07" every single visit — true, and useless: for a studio
          selling a handful a month that is what almost every day says, so it
          became a sentence the eye stops reading, which is the worst place for
          the one line that has to be believed when something breaks.
          
          How current the figures are belongs on the chart, where the design
          puts it. What is left for here is the case where asking went wrong.
        */}
        {lastSync && !fine ? (
          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-[6px] w-[6px] flex-none rounded-full"
              style={{ background: 'var(--warn)' }}
            />
            <span className="text-[12px]" style={{ color: 'var(--warn)' }}>
              {SAID[lastSync.outcome] ?? lastSync.outcome}
            </span>
          </span>
        ) : null}
        {!lastSync ? (
          <span className="text-[12px]" style={{ color: 'var(--muted)' }}>
            Nothing has asked Apple yet
          </span>
        ) : null}
        <span className="ml-auto">{children}</span>
      </div>
      {/*
        Apple's own words, and only when they are needed. Once the credentials
        parse, everything still capable of being wrong is distinguishable only
        by what Apple said about it — but printing that on a healthy morning
        would be a paragraph of prose above the figures, every day.
      */}
      {lastSync && !fine && lastSync.detail ? (
        <p
          className="m-0 font-mono text-[11px] leading-[1.5] break-words"
          style={{ color: 'var(--warn)' }}
        >
          {OURS.has(lastSync.outcome) ? `This server: ${lastSync.detail}` : lastSync.detail}
        </p>
      ) : null}
    </div>
  );
};

/**
 * How long ago, in the design's own phrasing ("2 hours ago").
 *
 * Rounded hard on purpose: the question this answers is "is what I am looking
 * at current", and no version of that question is answered better by a minute
 * count than by "3 hours ago".
 */
const ago = (at: string, now: Date): string => {
  const minutes = Math.floor((now.getTime() - new Date(at).getTime()) / 60_000);
  if (!Number.isFinite(minutes) || minutes < 0) return 'just now';
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

/**
 * The change from one period to the next, as the design's `Δ` column.
 *
 * Three answers, not two, and the third is the one that matters most:
 *
 * - `null` — the earlier period was never fetched. Nothing can be said, and
 *   saying `+100%` because the comparison is against zero would be a figure
 *   invented out of an absence. Every app would show it on the morning the
 *   integration was switched on.
 * - `from === 0` — nothing sold then and something sells now. That is real
 *   growth and it has no percentage: division by zero is not infinity here,
 *   it is "new".
 * - otherwise the percentage, rounded.
 */
const change = (now: number, before: number | null): { text: string; tone: string } => {
  if (before === null) return { text: '—', tone: 'var(--dim)' };
  if (before === 0)
    return now === 0 ? { text: '—', tone: 'var(--dim)' } : { text: 'new', tone: 'var(--ok)' };
  const percent = Math.round(((now - before) / before) * 100);
  if (percent === 0) return { text: '0%', tone: 'var(--muted)' };
  return {
    text: `${percent > 0 ? '+' : ''}${percent}%`,
    tone: percent > 0 ? 'var(--ok)' : 'var(--warn)',
  };
};

/** The share a part is of a whole, as a whole number of percent. */
const shareOf = (part: number, whole: number): number =>
  whole > 0 ? Math.round((part / whole) * 100) : 0;

/** One row of a Top markets card: a code, a value, a share and a 3px bar. */
const MarketRow = ({
  code,
  value,
  share,
  colour,
}: {
  code: string;
  value: string;
  share: number | null;
  colour: string;
}) => (
  <span className="flex min-w-0 flex-col gap-[5px]">
    <span className="flex items-baseline gap-2.5">
      <span className="min-w-0 flex-1 text-[12.5px]">{code}</span>
      <span className="font-mono text-[12px] tabular-nums">{value}</span>
      <span
        className="w-[30px] text-right font-mono text-[10.5px] tabular-nums"
        style={{ color: 'var(--dim)' }}
      >
        {share === null ? '' : `${share}%`}
      </span>
    </span>
    <span className="block h-[3px] overflow-hidden rounded-sm" style={{ background: 'var(--in)' }}>
      <span className="block h-[3px]" style={{ width: `${share ?? 0}%`, background: colour }} />
    </span>
  </span>
);

const MarketCard = ({ by, children }: { by: string; children: React.ReactNode }) => (
  <Panel className="flex min-w-0 flex-col gap-2.5 px-4 py-3.5">
    <span className="flex items-baseline gap-2.5">
      <span className="text-[13px] font-semibold tracking-[-0.01em]">Top markets</span>
      <span className="font-mono text-[9.5px]" style={{ color: 'var(--muted)' }}>
        {by}
      </span>
    </span>
    {children}
  </Panel>
);

/**
 * The Overview as the v2 design draws it, filled with what Apple actually sent.
 *
 * Two of the design's five figures, three of its five table columns and the
 * whole Signals card have no source here. Every one of them keeps its place and
 * says so, because the alternative — a zero, a dash, an empty card — is a
 * figure, and fabricated figures are the thing this screen was rebuilt to stop
 * showing.
 */
const Figures = ({
  overview,
  metric,
  onMetric,
  updated,
}: {
  overview: AppStoreOverview;
  metric: Metric;
  onMetric: (metric: Metric) => void;
  updated: string | null;
}) => {
  const totalDownloads = overview.byMarket.reduce((all, one) => all + one.downloads, 0);

  return (
    <div className="flex flex-col gap-3">
      <Stats overview={overview} />

      {/*
        The chart takes the row on its own.

        The design puts a Signals card beside it — written judgements like
        "converts 22% better since the paywall change" — behind its own
        `showSignals` flag, and this is that flag off. Where those sentences
        would come from is a decision nobody has made, and a card explaining
        that it is empty is still a card asking to be read every visit.
      */}
      <Chart overview={overview} metric={metric} onMetric={onMetric} updated={updated} />

      <Panel className="overflow-hidden">
        <div
          className="grid gap-2.5 border-b px-3.5 py-[9px] text-[11px] font-medium"
          style={{
            gridTemplateColumns: 'minmax(0, 1.6fr) 0.9fr 0.9fr 0.7fr 0.7fr',
            background: 'var(--panel2)',
            borderColor: 'var(--line)',
            color: 'var(--muted)',
          }}
        >
          <span>App</span>
          <span>Units</span>
          <span>Proceeds</span>
          {/*
            The caveats live here, on hover, and not as a paragraph under the
            table. They matter to somebody deciding whether to trust a number,
            and to nobody else — and a screen that prints its own footnotes for
            every visitor is a screen apologising to people who had no
            question.
          */}
          <span title="Free trials that became paid, per app. A trial begun in an earlier period converts in this one, so this is not one group of people followed through.">
            Conv.
          </span>
          <span title="Change against the period immediately before this one. Blank until that period has been fetched too.">
            Δ
          </span>
        </div>
        {/*
          A period with nothing in it left the header row standing alone —
          five column names over a hairline and then the next card, which
          reads as a table that failed to load rather than a quiet week. Both
          markets cards below already say this; the table was the one place
          that said nothing.
        */}
        {overview.byApp.length === 0 ? (
          <p className="m-0 px-3.5 py-[13px] text-[12px]" style={{ color: 'var(--muted)' }}>
            No app had a download or a sale in this period.
          </p>
        ) : null}
        {overview.byApp.map((app, at) => (
          <div
            key={app.sku}
            className="grid items-center gap-2.5 px-3.5 py-[9px]"
            style={{
              gridTemplateColumns: 'minmax(0, 1.6fr) 0.9fr 0.9fr 0.7fr 0.7fr',
              borderBottom: at === overview.byApp.length - 1 ? undefined : '1px solid var(--line)',
            }}
          >
            {/*
              The website's own app, once its Apple ID is linked: the name
              the admin uses, and a way in. Apple's title otherwise, with a
              word that says the row belongs to no app here yet.
            */}
            {app.linkedApp ? (
              <Link
                to={`/apps/${app.linkedApp.id}`}
                className="dim-link min-w-0 truncate text-[12.5px] font-medium no-underline hover:underline"
                style={{ color: 'var(--text)' }}
                title={`${app.title} · SKU ${app.sku}`}
              >
                {app.linkedApp.name}
              </Link>
            ) : (
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="min-w-0 truncate text-[12.5px] font-medium">{app.title}</span>
                <span
                  className="flex-none font-mono text-[9.5px] tracking-[0.05em] uppercase"
                  style={{ color: 'var(--dim)' }}
                  title={app.appleId ? `Apple ID ${app.appleId} is on no app here` : undefined}
                >
                  not linked
                </span>
              </span>
            )}
            <span className="font-mono text-[12px] tabular-nums">{grouped(app.downloads)}</span>
            <span className="font-mono text-[12px] tabular-nums">
              {app.proceeds.map((one) => money(one)).join(' · ') || '—'}
            </span>
            {/*
              Joined on Apple's id, not the SKU. An app with several SKUs — a
              bundle, a regional edition — shows the same conversion on each
              row, which is true of the app and worth knowing beside a per-SKU
              download count. A dash wherever no trial began, which is every
              row for every studio without subscriptions.
            */}
            <span
              className="font-mono text-[11.5px] font-semibold tabular-nums"
              style={{ color: app.trialConversion === null ? 'var(--dim)' : 'var(--text)' }}
            >
              {app.trialConversion === null ? '—' : `${app.trialConversion}%`}
            </span>
            <span
              className="font-mono text-[11.5px] tabular-nums"
              style={{ color: change(app.downloads, app.previousDownloads).tone }}
              title={
                app.previousDownloads === null
                  ? 'The period before this one has not been fetched'
                  : `${grouped(app.previousDownloads)} in the period before`
              }
            >
              {change(app.downloads, app.previousDownloads).text}
            </span>
          </div>
        ))}
      </Panel>

      <div
        className="grid items-start gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
      >
        <MarketCard by="by downloads">
          {overview.byMarket.length > 0 ? (
            overview.byMarket.map((market) => (
              <MarketRow
                key={market.countryCode}
                code={market.countryCode}
                value={grouped(market.downloads)}
                share={shareOf(market.downloads, totalDownloads)}
                colour="var(--ok)"
              />
            ))
          ) : (
            <span className="text-[12px]" style={{ color: 'var(--muted)' }}>
              No downloads in this period.
            </span>
          )}
        </MarketCard>
        <MarketCard by="by revenue">
          {overview.byMarketProceeds.length > 0 ? (
            overview.byMarketProceeds.map((market) => (
              <MarketRow
                key={market.countryCode}
                code={market.countryCode}
                value={market.proceeds.map((one) => money(one)).join(' · ')}
                /*
                 * No share for money. A percentage needs one total, and this
                 * list can hold several currencies — a share across them would
                 * be a ratio of unlike things.
                 */
                share={null}
                colour="var(--accent)"
              />
            ))
          ) : (
            <span className="text-[12px]" style={{ color: 'var(--muted)' }}>
              No proceeds in this period — every download was of a free app.
            </span>
          )}
        </MarketCard>
      </div>
    </div>
  );
};

/**
 * What the last attempt at a report came to, in a sentence.
 *
 * `outcome` is the server's own word, and each one is a different afternoon.
 * `unauthorised` and `forbidden` are the pair that read alike and are not: one
 * is the key, the other is the role, and only one of them means generating a
 * new key. `unexpected` is the honest hole — Apple documents no 404 for this
 * endpoint, so what it answers for a day it has not published is something this
 * project intends to learn from a real run rather than guess at.
 */
const SAID: Record<string, string> = {
  report: 'Apple sent a report',
  noSales: 'Apple has that day, and there were no sales on it',
  notReady: 'Apple has not published that day yet',
  unauthorised: 'Apple did not accept the key',
  forbidden: 'The key is valid, but its role cannot read Sales and Trends',
  rateLimited: 'Apple is rate limiting this key',
  badRequest: 'Apple refused the request',
  unexpected: 'Apple answered something this project has not seen before',
  unreachable: 'The server could not reach Apple',
  unreadable: 'Apple sent a report this server could not read',
  unsignable: 'The server could not sign a token with the private key',
  unstorable: 'The report arrived, but this server could not store it',
};

/**
 * Which outcomes are Apple speaking, and which are this server.
 *
 * Not cosmetic. `detail` is printed underneath under a comment calling it
 * Apple's own words, and for three of these it is not: `unsignable` never
 * reaches Apple, `unstorable` is a Postgres error, `unreachable` is this
 * machine's network stack. All three were announced as "Apple answered:
 * unstorable" with a duplicate-key message underneath attributed to Apple —
 * which sends somebody to App Store Connect to debug a database.
 */
const OURS = new Set(['unreachable', 'unsignable', 'unstorable']);

/**
 * What the screen says once the four credentials are in place.
 *
 * Deliberately not figures, even now that reports are fetched. The credentials
 * being readable is not the same claim as a report having arrived, and a report
 * having arrived is not the same as there being anything to show — a studio
 * that connects on a Tuesday has no report until Apple publishes one, and a
 * quiet Sunday is an empty report rather than a broken integration. Each of
 * those is said in its own words rather than collapsed into a zero.
 *
 * `stale` is the case where this answer is the last good one and a refresh has
 * since failed. Throwing it away for "could not check" would trade a true
 * answer for an unknown one.
 */
const Connected = ({
  keyId,
  stale,
  lastSync,
  onSync,
  syncing,
  overview,
  metric,
  onMetric,
  range,
  onRange,
}: {
  keyId: string | null;
  stale?: boolean;
  lastSync: AppStoreSyncRun | null;
  onSync: () => void;
  syncing: boolean;
  overview: AppStoreOverview | undefined;
  metric: Metric;
  onMetric: (metric: Metric) => void;
  range: string;
  onRange: (range: string) => void;
}) => (
  <div className="flex flex-col gap-5">
    {/*
      The figures come first when there are any, and the setup below them.

      When there are none the heading stays "configured", which is the honest
      thing to lead with: a studio that connected an hour ago has a working key
      and no data, and putting an empty figures panel at the top would say the
      opposite in the most confident place on the screen.
    */}
    {overview && overview.dayCount > 0 ? (
      <>
        {/*
          The design puts the range tabs in the shell's header. This page owns
          them because only this page has a range: the shell is shared with
          every other screen, and a control that belongs to one of them does
          not belong in something all of them render.

          The sync state shares the row, because it is the one control here
          somebody presses and it spent a round at the very bottom of the page.
        */}
        <SyncBar lastSync={lastSync} onSync={onSync} syncing={syncing}>
          <span
            className="mr-2.5 font-mono text-[10.5px]"
            style={{ color: 'var(--dim)' }}
            title={`${overview.dayCount} day${overview.dayCount === 1 ? '' : 's'} of figures. Gaps are days Apple had not published when this last looked.`}
          >
            {dateRange(overview.from, overview.to)}
          </span>
          <span
            className="inline-flex rounded-lg border p-0.5"
            style={{ background: 'var(--panel2)', borderColor: 'var(--line)' }}
          >
            {RANGES.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => onRange(key)}
                aria-pressed={range === key}
                className="focus-ring min-h-[26px] cursor-pointer rounded-md border-0 px-[9px] font-mono text-[10.5px]"
                style={
                  range === key
                    ? { background: 'var(--accent)', color: 'var(--on-accent)' }
                    : { background: 'transparent', color: 'var(--muted)' }
                }
              >
                {label}
              </button>
            ))}
          </span>
        </SyncBar>
        <Figures
          overview={overview}
          metric={metric}
          onMetric={onMetric}
          updated={lastSync ? ago(lastSync.ranAt, new Date()) : null}
        />
      </>
    ) : (
      <div className="flex flex-col gap-4">
        {/*
          The same bar, without the period tabs — there is no period to choose
          between when nothing has been fetched.

          It belongs here more than anywhere: a studio that has just configured
          a key and has no figures yet is exactly the one that wants to press
          "Sync now", and for one round removing the panel at the bottom of the
          page took the button away from them entirely.
        */}
        <SyncBar lastSync={lastSync} onSync={onSync} syncing={syncing}>
          {null}
        </SyncBar>
        <h2 className="m-0 text-[15px] font-semibold tracking-[-0.01em]">
          App Store Connect is configured
        </h2>
        <p className="m-0 text-[12.5px] leading-[1.6]" style={{ color: 'var(--muted)' }}>
          The server holds an issuer id, a vendor number and a private key it can sign with
          {keyId ? (
            <>
              {' '}
              — key <span className="font-mono">{keyId}</span>
            </>
          ) : null}
          . It asks Apple for the last few days of the Summary Sales Report every six hours, and
          stores what comes back — but no day has been stored yet, so there is no figure here rather
          than a zero standing in for one.
        </p>
      </div>
    )}
    {stale ? (
      <p className="m-0 text-[12px] leading-[1.6]" style={{ color: 'var(--warn)' }}>
        Could not refresh this just now — it is the last answer the server gave.
      </p>
    ) : null}
  </div>
);

/**
 * A line at the top of Overview when somebody is waiting for an answer.
 *
 * Only when there is somebody: a strip saying "0 reports waiting" every day
 * teaches people to stop reading the strip, and the rail already carries the
 * count for the days it is not nought.
 */
function WaitingReports() {
  const waiting = useWaitingTickets();
  if (waiting === 0) return null;

  return (
    <Link
      to="/tickets"
      className="focus-ring flex max-w-[760px] items-center justify-between gap-3 rounded-xl border px-4 py-3 text-[13px] no-underline"
      style={{ borderColor: 'var(--accent)', background: 'var(--accent-soft)' }}
    >
      <span>
        {waiting === 1
          ? 'One report from the website is waiting for an answer.'
          : `${waiting} reports from the website are waiting for an answer.`}
      </span>
      <span style={{ color: 'var(--accent)' }}>Open Tickets →</span>
    </Link>
  );
}

export default function DashboardPage() {
  const status = useAppStoreStatus();
  const sync = useSyncAppStore();
  // The design's third tab, and the one a studio looks at by default.
  const [range, setRange] = useState<string>('30d');
  const [metric, setMetric] = useState<'downloads' | 'proceeds'>('downloads');
  // Only for a studio that has a key: five aggregate queries are not worth
  // running behind a setup guide.
  const overview = useAppStoreOverview(status.data?.connected === true, range);

  /*
   * Two shapes, not one.
   *
   * With figures this is the design's dashboard: full-width sections, panels of
   * their own, nothing wrapping them. Without them it is a single bordered card
   * holding a setup guide — a page of prose, which wants a measure it can be
   * read at rather than the width of the window.
   *
   * They were the same container for one round, and the figures inherited the
   * card's `max-w-[760px]`: the strip of five statistics wrapped onto two rows
   * inside a box, with the wrapped cells keeping borders that then led nowhere.
   */
  const showingFigures = status.data?.connected === true && (overview.data?.dayCount ?? 0) > 0;

  if (showingFigures && status.data) {
    return (
      <section className="flex flex-col gap-3 px-[18px] pt-3.5 pb-6">
        <WaitingReports />
        <Connected
          keyId={status.data.keyId}
          stale={status.isError}
          lastSync={status.data.lastSync}
          onSync={() => sync.mutate()}
          syncing={sync.isPending}
          overview={overview.data}
          metric={metric}
          onMetric={setMetric}
          range={range}
          onRange={setRange}
        />
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3 px-[18px] pt-3.5 pb-6">
      <WaitingReports />
      <div
        className="flex max-w-[760px] flex-col gap-5 rounded-xl border px-5 py-[18px]"
        style={{ background: 'var(--panel)', borderColor: 'var(--line)' }}
      >
        {/*
          Three states, not two, and the third is the one that was wrong.

          `status.data?.connected` is `undefined` while the question is still
          being asked **and** when asking it failed — so both drew the setup
          guide, which is not a neutral thing to draw. It is a screen asserting
          that this studio has not connected App Store Connect. A configured
          studio saw the whole six-step guide flash on every visit, and a studio
          whose session had expired saw it permanently, with no way to tell it
          apart from the truth and every reason to go and set the key up again.

          `isPending` alone does not cover the second one: a failed query is not
          pending. That was written into a comment in `AdminShell.spec` as
          though it were, which is how a guard nobody has gets believed in.
        */}
        {status.isPending ? (
          <Asking />
        ) : status.isError && !status.data ? (
          <Unknown onRetry={() => void status.refetch()} />
        ) : status.data?.connected ? (
          <Connected
            keyId={status.data.keyId}
            stale={status.isError}
            lastSync={status.data.lastSync}
            onSync={() => sync.mutate()}
            syncing={sync.isPending}
            overview={overview.data}
            metric={metric}
            onMetric={setMetric}
            range={range}
            onRange={setRange}
          />
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <h2 className="m-0 text-[15px] font-semibold tracking-[-0.01em]">
                App Store Connect is not connected
              </h2>
              <p className="m-0 text-[12.5px] leading-[1.6]" style={{ color: 'var(--muted)' }}>
                Every figure this screen is meant to show — downloads, proceeds, subscriptions,
                refunds — comes from Apple, and nothing in this project has asked Apple for it yet.
                Until it is connected there is no App Store figure to put here, so there is none.
                Here is what connecting it takes — every step read off Apple's own documentation,
                linked at the bottom so it can be checked rather than trusted.
              </p>
            </div>

            <Section title="Setting it up">
              <Steps items={STEPS} numbered />
            </Section>

            <Section title="What the server then does">
              <Steps items={THEN} />
            </Section>

            {/*
          Linked, not paraphrased and left at that. Apple changes this and the
          steps above are a snapshot of the day they were read — a sentence that
          has quietly stopped being true is the failure this whole screen exists
          to undo.
        */}
            <Section title="Apple's documentation">
              <p className="m-0 flex flex-wrap gap-x-3 gap-y-1 text-[11.5px]">
                {SOURCES.map(([label, href]) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="focus-ring"
                    style={{ color: 'var(--accent)' }}
                  >
                    {label}
                  </a>
                ))}
              </p>
            </Section>
          </>
        )}

        <p
          className="m-0 border-t pt-3.5 text-[12px] leading-[1.6]"
          style={{ borderColor: 'var(--line)', color: 'var(--muted)' }}
        >
          The catalogue and the blog are real and editable now:{' '}
          <Link to="/apps" className="prose-link focus-ring" style={{ color: 'var(--accent)' }}>
            Apps
          </Link>{' '}
          and{' '}
          <Link to="/blog" className="prose-link focus-ring" style={{ color: 'var(--accent)' }}>
            Blog
          </Link>
          .
        </p>
      </div>
    </section>
  );
}
